import type { InsightsClient, InsightsMethodMap } from "search-insights";

import { authStore } from "@/auth";
import { BOT_DEVICE_ID } from "@/auth/constants";
import {
  AnalyticsMe,
  AnalyticsProduct,
  AnalyticsProvider,
} from "@/common/contracts/analytics.contracts";
import { ALGOLIA_API_KEY, ALGOLIA_APP_ID, GRAPHQL_ENDPOINT } from "@/config";

/*
 * `search-insights` is loaded on the first event, not at module load. The
 * analytics service is imported from the app shell, so a static import put the
 * 10 KB client into the shared _app chunk of every page for visitors who never
 * trigger a tracked event (WHO-5479, S5). Every call is queued on the same
 * promise, so `init` still runs before the event that needed it and calls keep
 * their order. A failed download only loses telemetry.
 */
let insightsClient: Promise<InsightsClient> | undefined;

const withInsights = (call: (insights: InsightsClient) => void) => {
  insightsClient ??= import("search-insights").then(module => module.default);
  insightsClient.then(call).catch((error: unknown) => {
    console.warn(
      "algoliaAnalyticsProvider -> search-insights unavailable",
      error
    );
  });
};

let algoliaInitialized = false;

// TODO: Un-hardcode them somehow
const guessIndexName = () => {
  if (GRAPHQL_ENDPOINT.includes("testing")) {
    return "whoppah-testing-nl";
  } else if (GRAPHQL_ENDPOINT.includes("production")) {
    return "whoppah-production-nl";
  }
};

const algoliaEvent = (
  key: keyof InsightsMethodMap,
  params: Record<string, unknown>
) => {
  if (!ALGOLIA_APP_ID || !ALGOLIA_API_KEY) {
    return;
  }

  const { userId, deviceId } = authStore.getState();

  // No need to track bots
  if (deviceId === BOT_DEVICE_ID) {
    return;
  }

  if (!algoliaInitialized) {
    withInsights(insights =>
      insights("init", {
        appId: ALGOLIA_APP_ID,
        apiKey: ALGOLIA_API_KEY,
        userToken: deviceId ?? undefined,
        authenticatedUserToken: userId ?? undefined,
      })
    );

    algoliaInitialized = true;
  }

  const options = {
    ...params,
    index: params.index ?? guessIndexName(),
    userToken: deviceId,
    authenticatedUserToken: userId,
  };

  withInsights(insights => insights(key, options));
};

/**
 * Algolia's implementation of event tracking provider.
 */
export const algoliaAnalyticsProvider: AnalyticsProvider = {
  trackAddToCart: (product: AnalyticsProduct) => {
    algoliaEvent("convertedObjectIDs", {
      eventName: "Product Added to Cart",
      objectIDs: [product.id],
    });
  },
  trackSelectItem: (
    position: number,
    listId: string,
    listName: string,
    product: AnalyticsProduct,
    searchMeta: {
      queryId?: string;
      indexName?: string;
      page?: number;
      limit?: number;
    }
  ) => {
    const { page, limit, queryId, indexName } = searchMeta;

    if (page && queryId && limit) {
      // Triggered for all kinds of search results.
      algoliaEvent("clickedObjectIDsAfterSearch", {
        eventName: "Product Clicked",
        objectIDs: [product.id],
        positions: [(page - 1) * limit + position],
        queryID: queryId,
        index: indexName,
      });
    } else {
      // Triggered for things like favorites also.
      algoliaEvent("clickedObjectIDs", {
        eventName: "Product Clicked",
        objectIDs: [product.id],
        index: indexName,
      });
    }
  },
  trackViewItem: (product: AnalyticsProduct) => {
    algoliaEvent("viewedObjectIDs", {
      eventName: "Product Viewed",
      objectIDs: [product.id],
    });
  },
  identify: (me: AnalyticsMe) => {
    withInsights(insights => insights("setAuthenticatedUserToken", me.id));
  },
  trackLogin: (me: AnalyticsMe) => {
    withInsights(insights => insights("setAuthenticatedUserToken", me.id));
  },
  trackLogout: () => {
    withInsights(insights => insights("setAuthenticatedUserToken", undefined));
  },
};
